import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, map, take } from 'rxjs';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { ConfirmationModalComponent } from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { PDFBoardStrategy } from 'src/app/models/upload';
import { PdfArrangement } from 'src/app/sessions/session/pdf-canvas-item/pdf-services/board-pdf-factory.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BulkUploadPageSelectionComponent } from './bulk-upload-page-selection/bulk-upload-page-selection.component';
import { BulkUploadSubmitService } from './services/bulk-upload-submit.service';
import { BulkUploadFile, BulkUploadService, BulkUploadStep } from './services/bulk-upload.service';
import { BulkUploadDropComponent } from './bulk-upload-drop/bulk-upload-drop.component';

@UntilDestroy()
@Component({
  selector: 'app-bulk-upload-panel',
  templateUrl: './bulk-upload-panel.component.html',
  styleUrls: ['./bulk-upload-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUploadPanelComponent {
  @ViewChild(BulkUploadDropComponent) dropComponent?: BulkUploadDropComponent;
  @ViewChild(BulkUploadPageSelectionComponent)
  pageSelectionComponent?: BulkUploadPageSelectionComponent;

  // Core state streams from the bulk upload service.
  readonly currentStep$ = this.bulkUploadService.currentStep$;
  readonly files$ = this.bulkUploadService.files$;
  readonly selectedSingleNonPdfFile$ = this.bulkUploadService.selectedSingleNonPdfFile$;
  // Footer visibility depends on step and whether files are present.
  readonly showFooter$ = combineLatest([this.currentStep$, this.files$]).pipe(
    map(([step, files]) => {
      if (step === BulkUploadStep.Processing) {
        return false;
      }
      return step !== BulkUploadStep.Drop || files.length > 0;
    }),
  );

  readonly BulkUploadStep = BulkUploadStep;
  protected readonly IconTypes = IconTypes;
  // Selection counts used in footer CTA logic.
  readonly selectedPagesCount$ = this.bulkUploadService.selectedPagesCount$;
  readonly selectedFilesCount$ = this.bulkUploadService.selectedFilesCount$;
  readonly isSingleFile$ = this.selectedFilesCount$.pipe(map((count) => count === 1));
  readonly selectedInsertStrategy$ = new BehaviorSubject<PDFBoardStrategy>(
    PDFBoardStrategy.CurrentBoard,
  );
  // Footer CTA label/icon derived from current step and strategy.
  readonly nextButtonState$ = combineLatest([
    this.currentStep$,
    this.selectedPagesCount$,
    this.isSingleFile$,
    this.selectedInsertStrategy$,
  ]).pipe(
    map(([step, selectedPagesCount, isSingleFile, strategy]) => {
      if (this.shouldShowUploadAction(step, isSingleFile, strategy)) {
        return {
          label: this.translateService.instant('Upload Pages ({{count}})', {
            count: selectedPagesCount,
          }),
          icon: 'file_upload',
        };
      }
      return {
        label: this.translateService.instant('Next'),
        icon: 'arrow_forward',
      };
    }),
  );
  // Blocks "Next" when no pages are selected.
  readonly hasPageSelection$ = new BehaviorSubject<boolean>(true);
  readonly canProceed$ = combineLatest([this.currentStep$, this.hasPageSelection$]).pipe(
    map(([step, hasSelection]) => step !== BulkUploadStep.PageSelection || hasSelection),
  );
  selectedInsertStrategy: PDFBoardStrategy = PDFBoardStrategy.CurrentBoard;
  uploadAsSeparateImages = true;
  selectedArrangement: PdfArrangement = PdfArrangement.Columns;

  constructor(
    private readonly bulkUploadService: BulkUploadService,
    private readonly bulkUploadSubmitService: BulkUploadSubmitService,
    private readonly sharedDataService: SessionSharedDataService,
    private readonly dialog: MatDialog,
    private readonly translateService: TranslateService,
  ) {}

  async handleImportSelected() {
    const files = this.bulkUploadService.files$.getValue();
    // Single non-PDF goes through the legacy single-file import.
    if (files.length === 1) {
      await this.bulkUploadSubmitService.submitSingleFile(files[0].file);
    }
    this.bulkUploadService.resetUploadFlow();
    this.sharedDataService.changeLeftPanelView.next(undefined);
  }

  handleSelectAnotherFile() {
    this.dropComponent?.openFilePicker();
  }

  handleNext() {
    const currentStep = this.bulkUploadService.currentStep$.getValue();
    // Step-specific handling keeps flow logic readable.
    switch (currentStep) {
      case BulkUploadStep.PageSelection:
        this.handlePageSelectionNext();
        return;
      case BulkUploadStep.InsertStrategy:
        this.handleInsertStrategyNext();
        return;
      case BulkUploadStep.Arrangement:
        this.handleArrangementNext();
        return;
      default:
        this.bulkUploadService.proceedToNextStep();
    }
  }

  private shouldShowUploadAction(
    step: BulkUploadStep,
    isSingleFile: boolean,
    strategy: PDFBoardStrategy,
  ) {
    if (step === BulkUploadStep.Arrangement) {
      return true;
    }
    if (step !== BulkUploadStep.InsertStrategy) {
      return false;
    }
    return isSingleFile || strategy === PDFBoardStrategy.OneBoardPerPage;
  }

  private handlePageSelectionNext() {
    const selection = this.pageSelectionComponent?.getSelectionState();
    if (!selection || selection.selectedFileIds.length === 0) {
      return;
    }
    this.bulkUploadService.applySelections(selection);
    this.bulkUploadService.proceedToNextStep();
  }

  private handleInsertStrategyNext() {
    const files = this.bulkUploadService.files$.getValue();
    if (!this.shouldSkipArrangement(files)) {
      this.bulkUploadService.proceedToNextStep();
      return;
    }
    this.submitAndClose(files);
  }

  private handleArrangementNext() {
    const files = this.bulkUploadService.files$.getValue();
    this.submitAndClose(files);
  }

  private shouldSkipArrangement(files: BulkUploadFile[]) {
    const selectedFilesCount = files.filter((file) => this.getSelectedPageCount(file) > 0).length;
    return (
      selectedFilesCount === 1 || this.selectedInsertStrategy === PDFBoardStrategy.OneBoardPerPage
    );
  }

  private submitAndClose(files: BulkUploadFile[]) {
    this.bulkUploadSubmitService.submit({
      files,
      strategy: this.selectedInsertStrategy,
      arrangement: this.selectedArrangement,
      uploadAsSeparateImages: this.uploadAsSeparateImages,
      renderInNearestFreeSpace: true,
    });
    this.bulkUploadService.resetUploadFlow();
    this.sharedDataService.changeLeftPanelView.next(undefined);
  }

  handleBack() {
    const currentStep = this.bulkUploadService.currentStep$.getValue();
    // Page selection uses a confirmation modal before reset.
    if (currentStep === BulkUploadStep.PageSelection) {
      this.openCancelUploadModal().then((confirmed) => {
        if (confirmed) {
          this.bulkUploadService.resetUploadFlow();
        }
      });
      return;
    }

    this.bulkUploadService.backStep();
  }

  handleSelectionChange(hasSelection: boolean) {
    this.hasPageSelection$.next(hasSelection);
  }

  handleStrategySelectionChange(selection: {
    strategy: PDFBoardStrategy;
    uploadAsSeparateImages: boolean;
  }) {
    this.selectedInsertStrategy = selection.strategy;
    this.selectedInsertStrategy$.next(selection.strategy);
    this.uploadAsSeparateImages = selection.uploadAsSeparateImages;
  }

  handleArrangementSelectionChange(arrangement: PdfArrangement) {
    this.selectedArrangement = arrangement;
  }

  handlePanelDragOver(event: DragEvent) {
    this.suppressDropOutsideDropStep(event);
  }

  handlePanelDrop(event: DragEvent) {
    this.suppressDropOutsideDropStep(event);
  }

  private getSelectedPageCount(file: { pages?: number[]; selectedPageIndices?: number[] }): number {
    if (file.selectedPageIndices) {
      return file.selectedPageIndices.length;
    }
    return file.pages?.length ?? 0;
  }

  private suppressDropOutsideDropStep(event: DragEvent) {
    if (this.bulkUploadService.currentStep$.getValue() === BulkUploadStep.Drop) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  private openCancelUploadModal(): Promise<boolean> {
    return new Promise((resolve) => {
      this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            title: this.translateService.instant('Cancel Document upload?'),
            description: this.translateService.instant(
              'Are you sure you want to continue? All of your documents will not be uploaded.',
            ),
            confirmButtonConfig: {
              icon: 'close',
              iconType: IconTypes.matIcon,
              label: this.translateService.instant('Cancel upload'),
              danger: true,
            },
            cancelButtonConfig: {
              icon: 'arrow_back',
              iconType: IconTypes.matIcon,
              label: this.translateService.instant('Go back'),
            },
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((output: 'confirm' | 'cancel' | 'close') => {
          resolve(output === 'confirm');
        });
    });
  }
}
